import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Calendar, Users, Trophy, Download, RefreshCcw, Filter,
  Phone, MapPin, CalendarClock, UserPlus, CheckCircle2, ChevronDown, ChevronUp,
  AlertTriangle, Clock, IndianRupee, PhoneCall,
} from 'lucide-react';
import api from '../../api';
import { encodeLan } from '../../api';
import { OwnerLayout } from '../../components/OwnerLayout';

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
];

function presetRange(key) {
  const now = new Date();
  if (key === 'today') return { from: ymd(now), to: ymd(now) };
  if (key === 'yesterday') {
    const y = new Date(now); y.setDate(now.getDate() - 1);
    return { from: ymd(y), to: ymd(y) };
  }
  if (key === 'week') {
    const day = now.getDay() || 7;
    const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
    return { from: ymd(monday), to: ymd(now) };
  }
  if (key === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: ymd(first), to: ymd(now) };
  }
  if (key === 'last30') {
    const from = new Date(now); from.setDate(now.getDate() - 29);
    return { from: ymd(from), to: ymd(now) };
  }
  return { from: ymd(now), to: ymd(now) };
}

const TYPE_META = {
  visit:      { label: 'Visit',      icon: CheckCircle2, color: '#16a34a', bg: '#dcfce7' },
  reschedule: { label: 'Reschedule', icon: CalendarClock, color: '#b45309', bg: '#fef3c7' },
  register:   { label: 'New Lead',   icon: UserPlus,      color: '#1d4ed8', bg: '#dbeafe' },
  onboard:    { label: 'Onboarded',  icon: Trophy,        color: '#7c3aed', bg: '#ede9fe' },
  recovery:   { label: 'Recovery',   icon: PhoneCall,     color: '#dc2626', bg: '#fee2e2' },
  payment:    { label: 'Payment',    icon: IndianRupee,   color: '#059669', bg: '#d1fae5' },
};

function formatINR(n) {
  const v = Number(n || 0);
  if (!isFinite(v)) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
  catch { return d; }
}
function formatTime(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }); }
  catch { return ''; }
}
function formatDaysAgo(d) {
  if (!d) return 'never';
  const diff = (Date.now() - new Date(d).getTime()) / 86400000;
  if (diff < 1) return 'today';
  if (diff < 2) return 'yesterday';
  return `${Math.floor(diff)}d ago`;
}

export default function OwnerTeamActivity() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('today');
  const [range, setRange] = useState(presetRange('today'));
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [data, setData] = useState({ items: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(true);

  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setRange(presetRange(key));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 5000 };
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      if (branchId) params.branchId = branchId;
      if (agentId) params.agentId = agentId;
      if (typeFilter) params.type = typeFilter;
      const res = await api.get('/reports/field-activity', { params });
      setData(res.data || { items: [], summary: null });
    } catch (err) {
      console.error(err);
      setData({ items: [], summary: null });
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, branchId, agentId, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const p = new URLSearchParams();
    if (range.from) p.set('from', range.from);
    if (range.to) p.set('to', range.to);
    if (branchId) p.set('branchId', branchId);
    if (agentId) p.set('agentId', agentId);
    if (typeFilter) p.set('type', typeFilter);
    p.set('format', 'csv');
    api.get(`/reports/field-activity?${p.toString()}`, { responseType: 'blob' }).then((res) => {
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `field_activity_${range.from || 'all'}_${range.to || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }).catch((e) => console.error(e));
  };

  const summary = useMemo(
    () => data.summary || { totalActions: 0, uniqueMerchants: 0, uniqueLoans: 0, uniqueAgents: 0, activeAgents: 0, idleAgents: 0, visitsWithReschedule: 0, paymentsAmount: 0, byType: {}, byAgent: [], byDay: [], byHour: [] },
    [data.summary]
  );
  const items = useMemo(() => data.items || [], [data.items]);

  // Agent dropdown built from summary (so it covers ALL agents in scope, not just active ones)
  const agentOptions = useMemo(() => {
    return (summary.byAgent || []).map(a => ({ id: a.agentId, name: a.name, employeeId: a.employeeId }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [summary.byAgent]);

  // Max for bar chart scaling
  const maxDayTotal = Math.max(1, ...(summary.byDay || []).map((d) => d.total));
  const maxHourTotal = Math.max(1, ...(summary.byHour || []).map((d) => d.count));
  const maxAgentTotal = Math.max(1, ...(summary.byAgent || []).map((a) => a.total));

  const visibleAgents = useMemo(() => {
    const arr = summary.byAgent || [];
    return showInactive ? arr : arr.filter(a => a.total > 0);
  }, [summary.byAgent, showInactive]);

  return (
    <OwnerLayout title="Team Activity">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Team Activity</h2>
          <p className="text-sm text-gray">Every visit, reschedule, registration & onboarding by your field team</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-outline" onClick={load} title="Refresh">
            <RefreshCcw size={14} style={{ marginRight: '4px' }} /> Refresh
          </button>
          <button className="btn-navy" onClick={downloadCsv} disabled={!items.length}>
            <Download size={14} style={{ marginRight: '4px' }} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="panel" style={{ padding: '12px 16px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: 'var(--g3)', fontWeight: 600 }}><Filter size={12} /> Range:</span>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p.key)}
              className={preset === p.key ? 'btn-navy' : 'btn-outline'}
              style={{ padding: '4px 10px', fontSize: '12px', minWidth: 'auto' }}
              data-testid={`ta-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <>
              <input
                type="date"
                className="form-input"
                style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
              <span>→</span>
              <input
                type="date"
                className="form-input"
                style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </>
          )}
          <div style={{ width: '1px', height: '20px', background: 'var(--g2)', margin: '0 4px' }} />
          {branches.length > 1 && (
            <select className="form-input" style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.branchName}</option>))}
            </select>
          )}
          <select className="form-input" style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }} value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            <option value="">All agents</option>
            {agentOptions.map((a) => (<option key={a.id} value={a.id}>{a.name}{a.employeeId ? ` (${a.employeeId})` : ''}</option>))}
          </select>
          <select className="form-input" style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            <option value="visit">Visits</option>
            <option value="reschedule">Reschedules</option>
            <option value="register">New Leads</option>
            <option value="onboard">Onboarded</option>
            <option value="recovery">Recovery Calls</option>
            <option value="payment">Payments</option>
          </select>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="od-stats" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        <div className="od-stat" style={{ borderLeft: '3px solid var(--navy)' }}>
          <div className="od-stat-lbl"><Activity size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Total Actions</div>
          <div className="od-stat-val">{summary.totalActions}</div>
          <div className="od-stat-sub">across {summary.uniqueAgents} {summary.uniqueAgents === 1 ? 'person' : 'people'}</div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl"><CheckCircle2 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Visits Done</div>
          <div className="od-stat-val" style={{ color: '#16a34a' }}>{summary.byType?.visit || 0}</div>
          <div className="od-stat-sub">
            {summary.visitsWithReschedule ? `${summary.visitsWithReschedule} with follow-up scheduled` : (summary.byType?.reschedule ? `${summary.byType.reschedule} pure reschedule${summary.byType.reschedule !== 1 ? 's' : ''}` : 'no follow-ups yet')}
          </div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl"><PhoneCall size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Recovery</div>
          <div className="od-stat-val" style={{ color: '#dc2626' }}>{summary.byType?.recovery || 0}</div>
          <div className="od-stat-sub">
            {(summary.byType?.payment || 0) > 0
              ? <>{summary.byType.payment} payment{summary.byType.payment !== 1 ? 's' : ''} · <IndianRupee size={10} style={{ verticalAlign: 'middle' }} />{formatINR(summary.paymentsAmount)}</>
              : 'no payments logged'}
          </div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl"><UserPlus size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />New Leads</div>
          <div className="od-stat-val" style={{ color: '#1d4ed8' }}>{summary.byType?.register || 0}</div>
          <div className="od-stat-sub">{summary.byType?.onboard || 0} onboarded</div>
        </div>
        <div className="od-stat" style={{ borderLeft: (summary.idleAgents || 0) > 0 ? '3px solid var(--red)' : '3px solid var(--g2)' }}>
          <div className="od-stat-lbl"><AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Idle</div>
          <div className="od-stat-val" style={{ color: (summary.idleAgents || 0) > 0 ? 'var(--red)' : 'var(--g3)' }}>{summary.idleAgents}</div>
          <div className="od-stat-sub">no activity in this range</div>
        </div>
      </div>

      {/* Type breakdown chips */}
      {summary.totalActions > 0 && (
        <div className="panel" style={{ marginTop: '12px', padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--g3)', fontWeight: 600, alignSelf: 'center' }}>Breakdown:</span>
            {Object.entries(summary.byType || {}).map(([k, v]) => {
              const meta = TYPE_META[k];
              if (!meta) return null;
              const pct = summary.totalActions ? Math.round((v / summary.totalActions) * 100) : 0;
              return (
                <span key={k} style={{ fontSize: '12px', padding: '4px 12px', background: meta.bg, color: meta.color, borderRadius: '999px', fontWeight: 600 }}>
                  <meta.icon size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  <strong>{meta.label}</strong> · {v} · {pct}%
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Daily trend */}
      {(summary.byDay?.length || 0) > 1 && (
        <div className="panel" style={{ marginTop: '12px' }}>
          <div className="panel-header">
            <span className="panel-title"><Calendar size={14} /> Daily Activity</span>
          </div>
          <div style={{ padding: '16px', display: 'flex', alignItems: 'flex-end', gap: '6px', minHeight: '160px' }}>
            {summary.byDay.map((d) => {
              const heightPct = (d.total / maxDayTotal) * 100;
              return (
                <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '40px' }}>
                  <div style={{ fontSize: '10px', color: 'var(--g2)', marginBottom: '2px', fontWeight: 600 }}>{d.total}</div>
                  <div title={`${d.date}: ${d.visits}v + ${d.reschedules}r + ${d.registers}n + ${d.onboards}o + ${d.recoveries || 0}c + ${d.payments || 0}p`}
                    style={{
                      width: '100%', minHeight: '4px',
                      height: `${Math.max(4, heightPct)}px`,
                      display: 'flex', flexDirection: 'column-reverse',
                      borderRadius: '4px 4px 0 0', overflow: 'hidden',
                    }}>
                    {d.visits > 0 && <div style={{ background: TYPE_META.visit.color, flexBasis: `${(d.visits / d.total) * 100}%` }} />}
                    {d.reschedules > 0 && <div style={{ background: TYPE_META.reschedule.color, flexBasis: `${(d.reschedules / d.total) * 100}%` }} />}
                    {d.registers > 0 && <div style={{ background: TYPE_META.register.color, flexBasis: `${(d.registers / d.total) * 100}%` }} />}
                    {d.onboards > 0 && <div style={{ background: TYPE_META.onboard.color, flexBasis: `${(d.onboards / d.total) * 100}%` }} />}
                    {(d.recoveries || 0) > 0 && <div style={{ background: TYPE_META.recovery.color, flexBasis: `${(d.recoveries / d.total) * 100}%` }} />}
                    {(d.payments || 0) > 0 && <div style={{ background: TYPE_META.payment.color, flexBasis: `${(d.payments / d.total) * 100}%` }} />}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--g3)', marginTop: '4px', whiteSpace: 'nowrap' }}>
                    {new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding: '0 16px 12px', display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '11px', color: 'var(--g3)' }}>
            {Object.entries(TYPE_META).map(([k, m]) => (
              <span key={k}><span style={{ display: 'inline-block', width: 10, height: 10, background: m.color, borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />{m.label}</span>
            ))}
          </div>
        </div>
      )}

      {/* Hour-of-day heatmap */}
      {summary.totalActions > 0 && (
        <div className="panel" style={{ marginTop: '12px' }}>
          <div className="panel-header">
            <span className="panel-title"><Clock size={14} /> Activity by Hour of Day</span>
          </div>
          <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-end', gap: '3px', minHeight: '90px' }}>
            {summary.byHour.map((h) => {
              const pct = (h.count / maxHourTotal) * 100;
              const isWorkHour = h.hour >= 9 && h.hour <= 19;
              return (
                <div key={h.hour} title={`${h.hour}:00 - ${h.count} action${h.count !== 1 ? 's' : ''}`}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '100%',
                    height: `${Math.max(2, pct * 0.7)}px`,
                    background: h.count === 0 ? 'var(--g1)' : (isWorkHour ? 'var(--navy)' : '#94a3b8'),
                    borderRadius: '2px',
                  }} />
                  <div style={{ fontSize: '9px', color: 'var(--g3)', marginTop: '3px' }}>
                    {h.hour % 3 === 0 ? `${h.hour}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding: '0 16px 10px', fontSize: '10px', color: 'var(--g3)' }}>
            Navy = work hours (9 AM - 7 PM). Grey bars after hours can hint at off-hours data entry.
          </div>
        </div>
      )}

      {/* Agent leaderboard */}
      <div className="panel" style={{ marginTop: '12px' }}>
        <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="panel-title"><Trophy size={14} /> Agent Performance</span>
          <label style={{ fontSize: '11px', color: 'var(--g3)', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Show idle agents
          </label>
        </div>
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '900px' }}>
          <thead>
            <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)', width: '40px' }}>#</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Team member</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Visits</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Resched.</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>New Leads</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Onboarded</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Recovery calls</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Payments</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Total</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {visibleAgents.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '24px', color: 'var(--g3)' }}>No team members to show</td></tr>
            )}
            {visibleAgents.map((a, i) => {
              const isIdle = a.total === 0;
              const isStale = a.daysInactive != null && a.daysInactive >= 3;
              return (
                <tr key={a.agentId} style={{ borderBottom: '1px solid var(--g2)', cursor: 'pointer', opacity: isIdle ? 0.7 : 1 }}
                    onClick={() => setAgentId(a.agentId)}
                    data-testid={`agent-row-${a.agentId}`}>
                  <td style={{ padding: '8px 12px', color: 'var(--g3)' }}>{i + 1}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>
                      {a.name}{a.employeeId ? ` (${a.employeeId})` : ''}
                      {a.role === 'manager' && (
                        <span style={{ marginLeft: 6, fontSize: '9.5px', padding: '1px 6px', background: '#eef2ff', color: '#4338ca', borderRadius: 999, fontWeight: 700, verticalAlign: 'middle' }}>MGR</span>
                      )}
                    </div>
                    {a.branchName && <div style={{ fontSize: '10.5px', color: 'var(--g3)' }}>{a.branchName}</div>}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: a.visits > 0 ? TYPE_META.visit.color : 'var(--g3)', fontWeight: a.visits > 0 ? 600 : 400 }}>{a.visits}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: a.reschedules > 0 ? TYPE_META.reschedule.color : 'var(--g3)' }}>{a.reschedules}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: a.registers > 0 ? TYPE_META.register.color : 'var(--g3)' }}>{a.registers}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: a.onboards > 0 ? TYPE_META.onboard.color : 'var(--g3)' }}>{a.onboards}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: (a.recoveries || 0) > 0 ? TYPE_META.recovery.color : 'var(--g3)', fontWeight: (a.recoveries || 0) > 0 ? 600 : 400 }}>{a.recoveries || 0}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: (a.payments || 0) > 0 ? TYPE_META.payment.color : 'var(--g3)' }}>
                    <div style={{ fontWeight: (a.payments || 0) > 0 ? 600 : 400 }}>{a.payments || 0}</div>
                    {(a.paymentsAmount || 0) > 0 && (
                      <div style={{ fontSize: '10.5px', color: 'var(--g3)', fontWeight: 400 }}>
                        <IndianRupee size={9} style={{ verticalAlign: 'middle' }} />{formatINR(a.paymentsAmount)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <strong style={{ color: 'var(--navy)' }}>{a.total}</strong>
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: '11.5px' }}>
                    {a.lastActiveAt ? (
                      <span style={{ color: isStale ? 'var(--red)' : 'var(--g3)' }}>
                        {isStale && <AlertTriangle size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />}
                        {formatDaysAgo(a.lastActiveAt)}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--red)', fontWeight: 600 }}>never</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Transactions table */}
      <div className="panel" style={{ marginTop: '12px', marginBottom: '24px' }}>
        <div className="panel-header">
          <span className="panel-title"><Activity size={14} /> Activity Log ({items.length})</span>
        </div>
        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>
            No activity in this date range. {(summary.idleAgents || 0) > 0 && (
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                {summary.idleAgents} agent{summary.idleAgents > 1 ? 's are' : ' is'} idle — they may not be using the portal.
              </div>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', minWidth: '720px' }}>
            <thead>
              <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>When</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Type</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Agent</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Merchant</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Note</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 250).map((i, idx) => {
                const meta = TYPE_META[i.type] || TYPE_META.visit;
                const isRecovery = i.type === 'recovery' || i.type === 'payment';
                return (
                  <tr key={`${i.merchantId || i.loanAccountNumber || 'x'}-${i.when}-${idx}`} style={{ borderBottom: '1px solid var(--g2)' }}>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: 'var(--g2)' }}>{formatDate(i.when)}</div>
                      <div style={{ fontSize: '10.5px', color: 'var(--g3)' }}>{formatTime(i.when)}</div>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ fontSize: '11px', padding: '2px 8px', background: meta.bg, color: meta.color, borderRadius: '999px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        <meta.icon size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                        {meta.label}
                      </span>
                      {i.type === 'payment' && i.amount ? (
                        <div style={{ fontSize: '11px', color: TYPE_META.payment.color, fontWeight: 700, marginTop: 3 }}>
                          <IndianRupee size={10} style={{ verticalAlign: 'middle' }} />{Number(i.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{i.agentName}</div>
                      {i.agentEmployeeId && <div style={{ fontSize: '10.5px', color: 'var(--g3)' }}>{i.agentEmployeeId}</div>}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ fontWeight: 600 }}>
                        {isRecovery ? (
                          <span style={{ cursor: i.loanAccountNumber ? 'pointer' : 'default', color: 'var(--navy)' }}
                            onClick={() => { if (i.loanAccountNumber) navigate(`/owner/recovery/cases/${encodeLan(i.loanAccountNumber)}`); }}>
                            {i.merchantName || '—'}
                          </span>
                        ) : (
                          <span style={{ cursor: i.merchantId ? 'pointer' : 'default', color: 'var(--navy)' }}
                            onClick={() => { if (i.merchantId) navigate(`/owner/merchants/${i.merchantId}/edit`); }}>
                            {i.merchantName || '—'}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '10.5px', color: 'var(--g3)' }}>
                        {isRecovery
                          ? (i.loanAccountNumber ? <>LAN {i.loanAccountNumber}{i.merchantPhone ? ` · ${i.merchantPhone}` : ''}</> : (i.merchantPhone || ''))
                          : (<>{i.ownerName}{i.merchantPhone ? ` · ${i.merchantPhone}` : ''}</>)}
                      </div>
                    </td>
                    <td style={{ padding: '8px 12px', maxWidth: '320px' }}>
                      <div style={{ color: 'var(--g2)' }}>{i.note || (i.type === 'payment' ? i.merchantStatus : '')}</div>
                      {i.rescheduledTo && (
                        <div style={{ fontSize: '10.5px', color: TYPE_META.reschedule.color, marginTop: '2px' }}>
                          → {i.type === 'recovery' ? 'next follow-up' : 'next visit'} {formatDate(i.rescheduledTo)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {items.length > 250 && (
            <div style={{ padding: '10px 16px', fontSize: '11px', color: 'var(--g3)', textAlign: 'center', borderTop: '1px solid var(--g2)' }}>
              Showing first 250 of {items.length} actions · export CSV for full list
            </div>
          )}
          </div>
        )}
      </div>
    </OwnerLayout>
  );
}
